import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { useAuth } from "@/contexts/AuthContext";
import type { LeadRules } from "@/lib/lead-queue";

/** Verdiene regelpanelet viser (RPC lead_rules, VIB-3050). */
export function useLeadRules(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["lead-rules"],
    queryFn: async (): Promise<LeadRules> => {
      const { data, error } = await supabase.rpc("lead_rules");
      if (error) throw new Error(error.message);
      return data as unknown as LeadRules;
    },
    enabled: options.enabled ?? true,
    staleTime: 5 * 60 * 1000,
  });
}

export interface QueueStatus {
  callable: number;
  low_water: number;
  high_water: number;
}

/** «Køen din: N ringbare» for innlogget booker. */
export function useLeadQueueStatus(options: { enabled?: boolean } = {}) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["lead-queue-status", user?.id],
    queryFn: async (): Promise<QueueStatus> => {
      const { data, error } = await supabase.rpc("lead_queue_status");
      if (error) throw new Error(error.message);
      return data as unknown as QueueStatus;
    },
    enabled: !!user?.id && (options.enabled ?? true),
  });
}

export interface BookerQueue extends QueueStatus {
  user_id: string;
  full_name: string;
  in_queue: number;
  low_overridden: boolean;
  high_overridden: boolean;
  /** null = alle felles pooler. */
  pool_ids: string[] | null;
  own_list_names: string[];
}

/** Admin: én rad per booker med kø, lavvann, høyvann og pooler. */
export function useLeadQueueOverview(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ["lead-queue-overview"],
    queryFn: async (): Promise<BookerQueue[]> => {
      const { data, error } = await supabase.rpc("lead_queue_overview");
      if (error) throw new Error(error.message);
      return (data as unknown as BookerQueue[]) ?? [];
    },
    enabled: options.enabled ?? true,
  });
}

export interface QueueSettingsInput {
  userId: string;
  /** null = standard fra reglene. */
  low: number | null;
  high: number | null;
  /** null = alle felles pooler. */
  poolIds: string[] | null;
}

/** «Bookerens kø og valg»: lagres via RPC, som logger aktøren. */
export function useSetLeadQueueSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: QueueSettingsInput) => {
      const { error } = await supabase.rpc("set_lead_queue_settings", {
        p_user_id: input.userId,
        p_low: input.low,
        p_high: input.high,
        p_pool_ids: input.poolIds,
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => invalidateFor(qc, "lead_queue_settings", {}),
    onError: (err: Error) => toast.error(err.message),
  });
}
